import { TimezoneOffsetError } from "@planta-v2/pds-ui/errors/ui-component.errors";

export class DateUtils {
  static splitDateTimeInTimezone(
    isoString: string,
    timezone: string,
  ): { readonly date: string; readonly time: string } {
    const dateObj = new Date(isoString);

    const date = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(dateObj);

    const time = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(dateObj);

    return { date, time };
  }

  static combineToUtcDate(date: string, time: string, timezone: string): Date {
    const tempDate = new Date(`${date}T${time}:00Z`);

    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "longOffset", // e.g., GMT-05:00
    });

    const parts = formatter.formatToParts(tempDate);
    const offsetPart = parts.find((part) => part.type === "timeZoneName");

    if (offsetPart === undefined) {
      throw new TimezoneOffsetError(timezone);
    }

    const rawOffset = offsetPart.value.replace("GMT", "");
    const offsetSuffix = rawOffset === "" ? "Z" : rawOffset;

    const isoStringWithOffset = `${date}T${time}:00${offsetSuffix}`;

    return new Date(isoStringWithOffset);
  }

  static splitEventSchedule(
    startIso: string,
    endIso: string,
    timezone: string,
  ): {
    readonly eventStartDate: string;
    readonly eventStartTime: string;
    readonly eventEndDate: string;
    readonly eventEndTime: string;
  } {
    const start = DateUtils.splitDateTimeInTimezone(startIso, timezone);
    const end = DateUtils.splitDateTimeInTimezone(endIso, timezone);
    return {
      eventStartDate: start.date,
      eventStartTime: start.time,
      eventEndDate: end.date,
      eventEndTime: end.time,
    };
  }

  static formatDate(date: Date, locale: string): string {
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  }

  static formatDateTime(date: Date, locale: string): string {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  }

  static formatRelativeDate(date: Date, locale: string): string {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    const diffMs = Date.now() - date.getTime();

    if (diffMs < 0) {
      return rtf.format(0, "day");
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return rtf.format(0, "minute");
    } else if (diffMinutes < 60) {
      return rtf.format(-diffMinutes, "minute");
    } else if (diffHours < 24) {
      return rtf.format(-diffHours, "hour");
    } else if (diffDays < 30) {
      return rtf.format(-diffDays, "day");
    } else {
      const months = Math.floor(diffDays / 30);
      return rtf.format(-months, "month");
    }
  }

  /**
   * Format a duration in minutes as a locale-aware human-readable string.
   *
   * Rules:
   * - < 60 min  → "X minutes" / "X minutos" (locale-aware plural)
   * - whole hours (X % 60 === 0) → "X hours" / "X horas"
   * - otherwise → "X.Y hours" (1 decimal)
   *
   * @example
   * DateUtils.formatDurationMinutes(45, 'en')   // '45 minutes'
   * DateUtils.formatDurationMinutes(60, 'en')   // '1 hour'
   * DateUtils.formatDurationMinutes(90, 'en')   // '1.5 hours'
   * DateUtils.formatDurationMinutes(1440, 'en') // '24 hours'
   */
  static formatDurationMinutes(minutes: number, locale: string): string {
    const [unit, value] =
      minutes < 60
        ? (["minute", minutes] as const)
        : ([
            "hour",
            Number.isInteger(minutes / 60)
              ? minutes / 60
              : Number((minutes / 60).toFixed(1)),
          ] as const);
    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit,
      unitDisplay: "long",
    }).format(value);
  }
}
